/* eslint-disable jsx-a11y/anchor-is-valid */

import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from 'react-redux';

// lib
import { Firestore } from "lib/firebase";

// exceptions
import { CountryError } from "shared/exceptions";

// reducers
import { selectCountry } from "hyc-store/reducers/countries/countriesReducer";

// components
import { Hero } from "shared/components/bulma/hero";
import { Button } from "shared/components/bulma/button";
import { ClipboardCopy } from "shared/pipes/clipboard-copy";
import { Accordion, AccordionItem } from "shared/components/bulma/accordion";

import { CustomerInfo } from "./customer-info";
import { CustomerStripe } from "./customer-stripe";
import { CustomerRequest } from "./customer-request";
import { CustomerPassword } from "./customer-password";
import { CustomerCheckoutLogs } from "./customer-checkout-logs";
import { CustomerBoxes } from "modules/customers/customer-boxes";
import { CustomerComments } from "shared/components/customers/comments";
import { CustomerStatusTag } from "shared/components/customers/misc/customer-status-tag";


const TABS = ['Info', 'Comments', 'Boxes', 'Settings'];
const DEFAULT_TAB = TABS[0];

const UserEditContainer = ({
    location,
    match: {
        params: { id: userId }
    }
}) => {
    const [customer, setCustomer] = useState({});
    const [activeTab, setActiveTab] = useState(
        new URLSearchParams(location.search).get('tab') || DEFAULT_TAB
    );

    const [stripe, setStripe] = useState({});
    const [fullName, setFullName] = useState('');
    const [loading, setLoading] = useState(true);
    const [lastUpdated, setLastUpdated] = useState('');

    const history = useHistory();
    const sessionCountry = useSelector(selectCountry);


    useEffect(() => {
        Firestore.collection("users")
            .doc(userId)
            .get()
            .then(result => {
                const resultData = result.data();
                setCustomer({ ...resultData, id: userId });

                // check country
                if (resultData.country && resultData.country !== sessionCountry) {
                    throw new CountryError();
                }

                setLoading(false);
                setFullName(resultData.full_name);
                setStripe(resultData.stripe);
            })
            .catch((err) => {
                if (err instanceof CountryError) {
                    history.push('/closet-hackers');
                }
            });
    }, [userId, history, sessionCountry, lastUpdated]);

    const LoadActiveComponent = () => {
        if (activeTab === 'Info') {
            return (
                <div className="mt-4">
                    <CustomerInfo customer={customer}
                        onUpdate={setLastUpdated} />
                </div>
            );
        }

        if (activeTab === 'Comments') {
            return (
                <Hero>
                    <CustomerComments edit={true} customer={customer} />
                </Hero>
            );
        }

        if (activeTab === 'Boxes') {
            return (
                <CustomerBoxes customerId={userId} />
            );
        }

        if (activeTab === 'Settings') {
            return (
                <Hero>
                    <Accordion>
                        <AccordionItem title="Password">
                            <CustomerPassword customer={customer} />
                        </AccordionItem>
                        <AccordionItem title="Stripe">
                            <CustomerStripe customer={customer} />
                        </AccordionItem>
                        <AccordionItem title="Request">
                            <CustomerRequest customer={customer} />
                        </AccordionItem>
                        <AccordionItem title="Checkout logs">
                            <CustomerCheckoutLogs customer={customer} />
                        </AccordionItem>
                    </Accordion>
                </Hero>
            );
        }

        return null;
    }

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        history.push({ search: `?tab=${tab}` });
    }

    const navigateStyleProfile = () => {
        history.push({ pathname: `/closet-hackers/${userId}/style` });
    }

    const Tabs = () => (
        <div className="tabs is-large is-right">
            <ul>
                {TABS.map((tab) => (
                    <li key={tab} className={tab === activeTab ? 'is-active' : ''}>
                        <a onClick={() => handleTabChange(tab)}>{tab}</a>
                    </li>
                ))}
            </ul>
        </div>
    );

    return (
        <>
            <Hero className="is-small is-light">
                <div className="container">
                    <div className="is-flex is-justify-content-space-between">
                        <div className="block">
                            <p className="is-size-5">
                                <ClipboardCopy value={fullName}>
                                    {fullName}
                                </ClipboardCopy>
                            </p>
                            <p><small>{customer.email}</small></p>
                            <p className="block mt-4 tags are-medium">
                                <CustomerStatusTag customer={{ stripe }} />
                                <p className="tag is-primary">{customer.delivery_week}</p>
                                <p className="tag is-info">{customer.distribution_partner}</p>
                            </p>
                        </div>

                        <Tabs />

                        <Button onClick={navigateStyleProfile} className="is-primary is-afloat-right">
                            <span className="icon-text">
                                <span className="icon">
                                    <i className="fas fa-vest-patches"></i>
                                </span>
                                <span>Style</span>
                            </span >
                        </Button>
                    </div>
                </div>
            </Hero>


            {loading && (
                <div className="container mt-6">
                    <progress className="progress is-large is-primary" max="100">60%</progress>
                </div>
            )}

            {!loading && <LoadActiveComponent />}
        </>
    );
};

export default UserEditContainer;
